import { Injectable, signal } from '@angular/core';
import { Outcome, OutcomeAtiBasis, OutcomeSourceType, OutcomeStatus, OutcomesState } from './outcomes.model';

function uuid(): string {
  return Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function seedOutcomes(): Outcome[] {
  return [
    {
      id: uuid(),
      clientId: '',
      description: 'Client successfully rolled over 401(k) to IRA, eliminating management fees.',
      tags: ['IRA', 'Rollover', 'Fee Reduction'],
      sourceType: 'ai',
      status: 'verified',
      dateAchieved: daysAgo(14),
      atiValue: 1200,
      atiBasis: 'annual',
      linkedActionId: null,
      timeToOutcomeDays: 21,
      createdBy: 'AIFA',
      updatedBy: 'AIFA',
      createdAt: daysAgo(14),
      updatedAt: daysAgo(14),
    },
    {
      id: uuid(),
      clientId: '',
      description: 'Client spoke with a tax specialist and gained clarity on deduction strategy.',
      tags: ['Tax', 'Education'],
      sourceType: 'human',
      status: 'verified',
      dateAchieved: daysAgo(7),
      atiValue: null,
      atiBasis: null,
      linkedActionId: null,
      timeToOutcomeDays: 7,
      createdBy: 'Jordan Lee',
      updatedBy: 'Jordan Lee',
      createdAt: daysAgo(7),
      updatedAt: daysAgo(7),
    },
    {
      id: uuid(),
      clientId: '',
      description: 'Increased 401(k) contribution rate from 6% to 8%, improving retirement trajectory.',
      tags: ['401k', 'Savings'],
      sourceType: 'ai',
      status: 'verified',
      dateAchieved: daysAgo(3),
      atiValue: 800,
      atiBasis: 'monthly',
      linkedActionId: null,
      timeToOutcomeDays: 30,
      createdBy: 'AIFA',
      updatedBy: 'AIFA',
      createdAt: daysAgo(3),
      updatedAt: daysAgo(3),
    },
    {
      id: uuid(),
      clientId: '',
      description: 'Emergency fund target established at 4 months of expenses.',
      tags: ['Emergency Fund'],
      sourceType: 'human',
      status: 'unverified',
      dateAchieved: today(),
      atiValue: null,
      atiBasis: null,
      linkedActionId: null,
      timeToOutcomeDays: null,
      createdBy: 'Alex Rivera',
      updatedBy: 'Alex Rivera',
      createdAt: today(),
      updatedAt: today(),
    },
  ];
}

const EMPTY_STATE: OutcomesState = { outcomes: [] };

@Injectable()
export class OutcomesStateService {
  private readonly _store = signal<Record<string, OutcomesState>>({});
  readonly storeSnapshot = this._store.asReadonly();

  ensureState(sessionId: string): void {
    if (this._store()[sessionId]) return;
    this._store.update((prev) => ({
      ...prev,
      [sessionId]: { outcomes: seedOutcomes() },
    }));
  }

  getState(sessionId: string): OutcomesState {
    return this._store()[sessionId] ?? EMPTY_STATE;
  }

  addOutcome(sessionId: string, partial: Omit<Outcome, 'id' | 'createdAt' | 'updatedAt'>): void {
    const now = today();
    const outcome: Outcome = {
      ...partial,
      id: uuid(),
      createdAt: now,
      updatedAt: now,
    };
    this._store.update((prev) => {
      const s = prev[sessionId] ?? EMPTY_STATE;
      return { ...prev, [sessionId]: { outcomes: [...s.outcomes, outcome] } };
    });
  }

  updateOutcome(sessionId: string, id: string, patch: Partial<Outcome>): void {
    this._store.update((prev) => {
      const s = prev[sessionId] ?? EMPTY_STATE;
      return {
        ...prev,
        [sessionId]: {
          outcomes: s.outcomes.map((o) =>
            o.id === id ? { ...o, ...patch, updatedAt: today() } : o
          ),
        },
      };
    });
  }

  /** Accepts an AI-generated outcome payload and appends it, defaulting status to verified. */
  ingestAiOutcome(sessionId: string, partial: Omit<Outcome, 'id' | 'createdAt' | 'updatedAt' | 'sourceType' | 'status'>): void {
    this.addOutcome(sessionId, {
      ...partial,
      sourceType: 'ai',
      status: 'verified',
    });
  }
}
